import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useAgentContext } from '@Providers';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  AgentConversation,
  AgentConversationInteractionsResponse,
  AgentEntry,
  GeneratedFile,
  GuidedJourney,
  ThinkingStep,
} from '@Models';
import { bopsApi } from '@Network';
import { setLastAnswer } from '../../Helpers/Agents/lastAnswer';
import { toApiThinkingSteps } from '../../Helpers/Agents/thinkingSteps';
import { AgentTurnPassthroughEvent, consumeAgentTurn } from '../AgentStreaming/agentTurn';
import { API_CONFIG, ERROR_PATTERNS } from '../AgentStreaming/constants';
import useAgents from '../useAgents';
import { useAgentConversations } from './useAgentConversations';
import { INTERACTION_LIMIT, useAgentInteractions } from './useAgentInteractions';
import useAgentRuntime from './useAgentRuntime';

const DEFAULT_CONVERSATION_TITLE = 'Untitled';

interface SendMessageCallbacks {
  onStreamUpdate?: (content: string) => void;
  onOptions?: (items: Array<{ id: string; label: string; hint?: string }>) => void;
  onMultiselect?: (items: Array<{ id: string; label: string; hint?: string }>) => void;
  onChoicegrid?: (
    items: Array<{ id: string; label: string; hint?: string; choices?: Array<{ id: string; label: string }> }>,
  ) => void;
  onFiles?: (files: GeneratedFile[]) => void;
  onRetract?: () => void;
  onThinkingSummary?: (steps: ThinkingStep[], seconds: number) => void;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

/**
 * Mirror the backend's `_generate_conversation_title` algorithm so the
 * optimistic title matches the server's: trim, collapse whitespace, cap at
 * 50 chars with an ellipsis. Without this, the sidebar title visibly
 * shortens after the post-stream refetch.
 */
const formatConversationTitle = (raw: string): string => {
  const cleaned = raw.trim().replace(/\s+/g, ' ');
  if (cleaned.length <= 50) return cleaned;
  return `${cleaned.slice(0, 47)}...`;
};

const createNewConversation = (conversationId: string, title?: string): AgentConversation => {
  const now = new Date().toISOString();

  return {
    conversation_id: conversationId,
    title: title ? formatConversationTitle(title) : DEFAULT_CONVERSATION_TITLE,
    timestamps: {
      created_at: now,
      updated_at: now,
      last_interaction_at: now,
    },
    agents_used: [],
    client_name: '',
    user_email: '',
  };
};

const addOptimisticInteraction = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  interactionTimestamp: string,
  userMessage: string,
  selectedAgent: AgentEntry | undefined,
) => {
  queryClient.setQueryData<AgentConversationInteractionsResponse>(
    ['agent-conversation-interactions', conversationId, INTERACTION_LIMIT],
    (oldData) => {
      const newInteraction: any = {
        conversation_id: conversationId,
        interaction_id: `temp_${interactionTimestamp}`,
        timestamp: interactionTimestamp,
        content: {
          input: userMessage,
          output: '',
        },
        agent_metadata: {
          endpoint_name: '',
          retailer_name: selectedAgent?.source_name || '',
          model: '',
        },
        feedback: null,
      };

      if (!oldData) {
        return {
          conversation_id: conversationId,
          interactions: [newInteraction],
        };
      }

      return {
        ...oldData,
        interactions: [...oldData.interactions, newInteraction],
      };
    },
  );
};

const removeOptimisticInteraction = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  interactionTimestamp: string,
) => {
  queryClient.setQueryData<AgentConversationInteractionsResponse>(
    ['agent-conversation-interactions', conversationId, INTERACTION_LIMIT],
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        interactions: oldData.interactions.filter(
          (interaction) => interaction.interaction_id !== `temp_${interactionTimestamp}`,
        ),
      };
    },
  );
};

const updateInteractionInCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  interactionTimestamp: string,
  updateFn: (interaction: any) => any,
) => {
  queryClient.setQueryData<AgentConversationInteractionsResponse>(
    ['agent-conversation-interactions', conversationId, INTERACTION_LIMIT],
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        interactions: oldData.interactions.map((interaction) =>
          interaction.timestamp === interactionTimestamp ? updateFn(interaction) : interaction,
        ),
      };
    },
  );
};

const createStreamingCallbacks = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  interactionTimestamp: string,
): SendMessageCallbacks => ({
  onStreamUpdate: (content: string) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      content: { ...interaction.content, output: content },
    }));
  },

  onOptions: (items) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      options: items,
    }));
  },

  onMultiselect: (items) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      multiselect_options: items,
    }));
  },

  onChoicegrid: (items) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      choicegrid_options: items,
    }));
  },

  onFiles: (files) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => {
      const existing = (interaction.generated_files ?? []) as GeneratedFile[];
      const byName = new Map<string, GeneratedFile>(existing.map((f) => [f.file_name, f]));
      files.forEach((f) => byName.set(f.file_name, f));
      return { ...interaction, generated_files: Array.from(byName.values()) };
    });
  },

  onRetract: () => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      options: undefined,
      multiselect_options: undefined,
      choicegrid_options: undefined,
    }));
  },

  onThinkingSummary: (steps, seconds) => {
    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      thinking_steps: toApiThinkingSteps(steps),
      thinking_seconds: seconds,
    }));
  },

  onComplete: () => {
    queryClient.invalidateQueries({ queryKey: ['agent-conversations'] }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error invalidating conversations after message complete:', error);
    });
  },

  onError: (error: string) => {
    // eslint-disable-next-line no-console
    console.error('Error sending message:', error);

    updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
      ...interaction,
      content: { ...interaction.content, output: `Error: ${error}` },
    }));
  },
});

interface RuntimeBindings {
  startedAtMs: number;
  setThinkingForConversation: (value: boolean) => void;
  appendStepForConversation: (step: ThinkingStep) => void;
}

const toOptionItems = (items: ReadonlyArray<{ id: string; label: string; hint?: string }>) =>
  items.map((it) => ({ id: it.id, label: it.label, ...(it.hint ? { hint: it.hint } : {}) }));

const handlePassthroughEvent = (
  event: AgentTurnPassthroughEvent,
  callbacks: SendMessageCallbacks,
  journeyCacheFile?: string,
  setPendingJourneyContext?: (value: string | null) => void,
): void => {
  switch (event.type) {
    case 'done':
      if (event.metrics?.cached && journeyCacheFile && setPendingJourneyContext) {
        setPendingJourneyContext(journeyCacheFile);
      }
      break;
    case 'files': {
      const generatedFiles: GeneratedFile[] = event.files.map((f) => ({
        file_name: f.name,
        file_size: f.size ?? 0,
        file_type: f.type ?? 'application/octet-stream',
      }));
      if (generatedFiles.length > 0) callbacks.onFiles?.(generatedFiles);
      break;
    }
    case 'options':
      callbacks.onOptions?.(toOptionItems(event.items));
      break;
    case 'multiselect':
      callbacks.onMultiselect?.(toOptionItems(event.items));
      break;
    case 'choicegrid':
      // `choices` is what makes a grid row actionable, so it is carried through.
      callbacks.onChoicegrid?.(
        event.items.map((it) => ({
          id: it.id,
          label: it.label,
          ...(it.hint ? { hint: it.hint } : {}),
          ...(it.choices ? { choices: it.choices.map((c) => ({ id: c.id, label: c.label })) } : {}),
        })),
      );
      break;
    default:
      break;
  }
};

const executeStreamingRequest = async (
  prompt: string,
  conversationId: string,
  selectedAgent: AgentEntry | undefined,
  abortSignal: AbortSignal,
  noResponseMessage: string,
  callbacks: SendMessageCallbacks,
  runtime: RuntimeBindings,
  displayText?: string,
  pendingJourneyContext?: string | null,
  setPendingJourneyContext?: (value: string | null) => void,
  selectedJourney?: string,
  journeyCacheFile?: string,
  interactionTimestamp?: string,
): Promise<void> => {
  const streamGenerator = bopsApi.streamAgentJSON(
    {
      prompt,
      conversation_id: conversationId,
      max_prompt_length: API_CONFIG.MAX_PROMPT_LENGTH,
      trace: API_CONFIG.TRACE_ENABLED,
      source_name: selectedAgent?.source_name || null,
      display_text: displayText,
      context_file: pendingJourneyContext || undefined,
      selected_journey: selectedJourney,
      journey_cache_file: journeyCacheFile,
      interaction_timestamp: interactionTimestamp,
    },
    abortSignal,
  );

  if (pendingJourneyContext && setPendingJourneyContext) {
    setPendingJourneyContext(null);
  }

  const result = await consumeAgentTurn(
    streamGenerator,
    {
      onAnswer: (text) => callbacks.onStreamUpdate?.(text),
      onThinkingChange: runtime.setThinkingForConversation,
      onStep: runtime.appendStepForConversation,
      onEvent: (event) => handlePassthroughEvent(event, callbacks, journeyCacheFile, setPendingJourneyContext),
      onRetract: () => callbacks.onRetract?.(),
    },
    { startedAtMs: runtime.startedAtMs, signal: abortSignal },
  );

  if (result.steps.length > 0) {
    callbacks.onThinkingSummary?.(result.steps, Math.max(1, Math.round(result.thinkingMs / 1000)));
  }

  if (!result.answer) {
    callbacks.onStreamUpdate?.(noResponseMessage);
  }
};

const isRequestCanceled = (err: unknown): boolean => {
  if (typeof err === 'object' && err !== null && ('name' in err || 'message' in err)) {
    const anyErr = err as { name?: string; message?: string };
    return (
      ERROR_PATTERNS.CANCELED_ERROR_NAMES.includes(anyErr.name as any) ||
      ERROR_PATTERNS.CANCELED_ERROR_MESSAGES.includes(anyErr.message as any)
    );
  }
  return false;
};

const getErrorMessage = (err: unknown, t: ReturnType<typeof useTranslation>['t'], lng: string): string => {
  if (!(err instanceof Error)) {
    return t('Agent.ErrorUnexpected', { lng });
  }

  const anyErr = err as Error & { code?: string };

  if (ERROR_PATTERNS.TIMEOUT_MESSAGES.some((msg) => err.message.includes(msg)) || anyErr.code === 'ECONNABORTED') {
    return t('Agent.ErrorTimeout', { lng });
  }

  if (err.message === ERROR_PATTERNS.NETWORK_ERROR_MESSAGE) {
    return t('Agent.ErrorNetwork', { lng });
  }

  // The backend refuses to run a guided journey whose instructions it cannot
  // load, rather than letting the agent improvise. Say so plainly — the generic
  // message would leave the user unsure whether anything ran.
  if (err.message.includes(ERROR_PATTERNS.JOURNEY_PROMPT_UNAVAILABLE)) {
    return t('Agent.ErrorJourneyUnavailable', { lng });
  }

  return t('Agent.ErrorUnexpected', { lng });
};

const useChatManagement = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { activeChatId, setActiveChatId, selectedAgent, setSelectedAgent } = useAgentContext();
  const { addConversationToCache, removeConversationFromCache, updateConversationInCache, conversations } =
    useAgentConversations(selectedAgent?.source_name);
  const { data: agents } = useAgents();
  const { isAccessDenied } = useAgentInteractions(activeChatId);
  const { getRuntime, isInFlight, startSend, setThinking, appendStep, finishSend, cancelSend, markRead } =
    useAgentRuntime();

  // Always-fresh ref for the active chat id — needed so finishSend can decide
  // markUnread synchronously regardless of which closure captured activeChatId.
  const activeChatIdRef = useRef(activeChatId);
  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  const [pendingJourneyContext, setPendingJourneyContext] = useState<string | null>(null);

  const sendUserMessage = useCallback(
    async (
      message: string,
      displayText?: string,
      selectedJourney?: string,
      journeyCacheFile?: string,
      existingConversationId?: string,
    ) => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) return;

      // Asking anything else is engagement: the previous answer was not abandoned.
      setLastAnswer(null);

      const wasNewConversation = !existingConversationId && (!activeChatId || isAccessDenied);
      let conversationId = existingConversationId || activeChatId;
      if (!conversationId || isAccessDenied) {
        conversationId = uuidv4();
        addConversationToCache(createNewConversation(conversationId, trimmedMessage));
        setActiveChatId(conversationId);
      }

      // Sync guard against double-sends in the same frame: if a controller
      // is already registered for this id, ignore the second invocation.
      // Reads from the ref instead of state so the guard holds before React
      // has flushed the `isLoading` update.
      if (isInFlight(conversationId)) return;

      const agentLng = (selectedAgent?.language || 'en').toLowerCase();
      const interactionTimestamp = new Date().toISOString();
      const startedAtMs = Date.now();
      const signal = startSend(conversationId, trimmedMessage, { interactionTimestamp });

      try {
        const userInputText = displayText || trimmedMessage;
        addOptimisticInteraction(queryClient, conversationId, interactionTimestamp, userInputText, selectedAgent);

        const prompt = trimmedMessage;
        const callbacks = createStreamingCallbacks(queryClient, conversationId, interactionTimestamp);

        const sendId = conversationId;

        await executeStreamingRequest(
          prompt,
          conversationId,
          selectedAgent,
          signal,
          t('Agent.ErrorNoResponse', { lng: agentLng }),
          callbacks,
          {
            startedAtMs,
            setThinkingForConversation: (v) => setThinking(sendId, v),
            appendStepForConversation: (step) => appendStep(sendId, step),
          },
          displayText,
          pendingJourneyContext,
          setPendingJourneyContext,
          selectedJourney,
          journeyCacheFile,
          interactionTimestamp,
        );

        callbacks.onComplete?.();
        // A cancelled or failed answer was never there to be abandoned.
        setLastAnswer({
          conversationId,
          interactionTimestamp,
          completedAtMs: Date.now(),
          actedSince: false,
        });
        finishSend(conversationId, activeChatIdRef.current === conversationId);
      } catch (err: unknown) {
        if (isRequestCanceled(err)) {
          // Cancel cleanup: optimistic interaction goes away. If the conversation
          // was created by THIS send, drop it from the sidebar so it doesn't ghost.
          removeOptimisticInteraction(queryClient, conversationId, interactionTimestamp);
          if (wasNewConversation) {
            removeConversationFromCache(conversationId);
            if (activeChatIdRef.current === conversationId) setActiveChatId(null);
          }
          return;
        }

        const errorMessage = getErrorMessage(err, t, agentLng);
        // eslint-disable-next-line no-console
        console.error('Error in sendUserMessage:', err);

        updateInteractionInCache(queryClient, conversationId, interactionTimestamp, (interaction) => ({
          ...interaction,
          content: { ...interaction.content, output: `Error: ${errorMessage}` },
        }));

        finishSend(conversationId, activeChatIdRef.current === conversationId);
      }
    },
    [
      activeChatId,
      setActiveChatId,
      selectedAgent,
      addConversationToCache,
      removeConversationFromCache,
      queryClient,
      pendingJourneyContext,
      setPendingJourneyContext,
      isAccessDenied,
      t,
      isInFlight,
      startSend,
      setThinking,
      appendStep,
      finishSend,
    ],
  );

  const executeJourney = useCallback(
    async (journey: GuidedJourney) => {
      try {
        const displayWithTitle = journey.title ? `${journey.display} - ${journey.title}` : journey.display;

        let conversationId = activeChatId;
        if (!conversationId) {
          conversationId = uuidv4();
          addConversationToCache(createNewConversation(conversationId, displayWithTitle));
          setActiveChatId(conversationId);
        }

        const cacheFilename = journey.cache?.s3_path?.filename;
        // Deliberately NOT journey.prompt. The backend loads the prompt from S3
        // itself on every journey turn (keyed off the journey id it receives
        // here), so sending it duplicated ~7.7KB into the request body — enough
        // to cross the 8KB WAF limit and have the whole request rejected at the
        // load balancer, with no server log and no usable error.
        await sendUserMessage(displayWithTitle, displayWithTitle, journey.id, cacheFilename, conversationId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error executing journey:', error);
      }
    },
    [activeChatId, setActiveChatId, addConversationToCache, sendUserMessage],
  );

  const switchChat = useCallback(
    (conversationId: string | null) => {
      setActiveChatId(conversationId);
      if (conversationId) markRead(conversationId);

      if (conversationId && agents) {
        const conversation = conversations.find((c) => c.conversation_id === conversationId);

        if (conversation && conversation.agents_used && conversation.agents_used.length > 0) {
          const firstAgentUsed = conversation.agents_used[0];
          const retailerName = firstAgentUsed.retailer_name;

          const agent = agents.find((a) => a.source_name === retailerName);

          if (agent) {
            setSelectedAgent(agent);
          }
        }
      }
    },
    [setActiveChatId, conversations, agents, setSelectedAgent, markRead],
  );

  /** Stops in-flight generation; returns the prompt so the caller can repopulate the input. */
  const stopGeneration = useCallback(
    (conversationId?: string): string | null => {
      const targetId = conversationId ?? activeChatIdRef.current;
      if (!targetId) return null;
      return cancelSend(targetId);
    },
    [cancelSend],
  );

  const deleteConversation = useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      await bopsApi.deleteConversation(conversationId);
    },

    onMutate: async (conversationId: string) => {
      await queryClient.cancelQueries({ queryKey: ['agent-conversations'] });
      const previousConversations = queryClient.getQueryData(['agent-conversations', 20]);
      // Abort any in-flight stream for this conversation and clear its runtime slot.
      cancelSend(conversationId);
      removeConversationFromCache(conversationId);
      if (activeChatId === conversationId) {
        setActiveChatId(null);
      }
      return { previousConversations, deletedConversationId: conversationId };
    },

    onError: (error, conversationId, context) => {
      // eslint-disable-next-line no-console
      console.error('Error deleting conversation:', error);
      if (context?.previousConversations) {
        queryClient.setQueryData(['agent-conversations', 20], context.previousConversations);
      }
      if (context?.deletedConversationId === conversationId && activeChatId === null) {
        setActiveChatId(conversationId);
      }
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: ['agent-conversations'] }),
  });

  const updateConversationTitle = useMutation({
    mutationFn: async ({ conversationId, title }: { conversationId: string; title: string }): Promise<void> => {
      await bopsApi.updateConversationTitle(conversationId, title);
    },

    onMutate: async ({ conversationId, title }) => {
      await queryClient.cancelQueries({ queryKey: ['agent-conversations'] });
      const previousConversations = queryClient.getQueryData(['agent-conversations', 20]);
      updateConversationInCache(conversationId, { title });
      return { previousConversations, conversationId, oldTitle: title };
    },

    onError: (error, _variables, context) => {
      // eslint-disable-next-line no-console
      console.error('Error updating conversation title:', error);
      if (context?.previousConversations) {
        queryClient.setQueryData(['agent-conversations', 20], context.previousConversations);
      }
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: ['agent-conversations'] }),
  });

  const activeRuntime = getRuntime(activeChatId);

  return {
    activeChatId,
    sendUserMessage,
    executeJourney,
    switchChat,
    stopGeneration,
    deleteConversation,
    updateConversationTitle,
    isGenerating: activeRuntime.isLoading,
    isThinking: activeRuntime.isThinking,
    thinkingSteps: activeRuntime.thinkingSteps,
    thinkingSeconds: activeRuntime.thinkingSeconds,
    turnTimestamp: activeRuntime.turnTimestamp,
    startedAtMs: activeRuntime.startedAtMs,
  };
};

export default useChatManagement;
